import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/shadcn/drawer';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu';
import { ScrollArea } from '@/components/ui/shadcn/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/shadcn/sheet';
import { Skeleton } from '@/components/ui/shadcn/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { cn, formatDate, logger } from '@/lib/utils';
import {
    Notification,
    NotificationCounts,
    NotificationFilter,
    NotificationGrouping,
    NotificationModuleFacet,
    NotificationResponse,
    NotificationSort,
} from '@/types/notification';
import { router, usePage } from '@inertiajs/react';
import {
    ArrowDownUp,
    Bell,
    Bug,
    Calculator,
    CalendarDays,
    CheckCheck,
    ChevronDown,
    ChevronRight,
    Cloud,
    Eye,
    EyeOff,
    KeyRound,
    LayoutList,
    MessageSquare,
    MoreVertical,
    ShieldAlert,
    Ticket,
    Trash2,
    TrendingUp,
    Wrench,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

interface NotificationSidebarProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/** Keyed by the module keys in resources/lang/*\/modules.php. */
const MODULE_ICONS: Record<string, typeof Bell> = {
    work_order: Wrench,
    service_request: Ticket,
    calculation_request: Calculator,
    lead: TrendingUp,
    vacation_request: CalendarDays,
    bug_report: Bug,
    comment: MessageSquare,
    account: KeyRound,
    security: ShieldAlert,
    integration: Cloud,
};

const GROUPING_STORAGE_KEY = 'crm.notifications.grouping';
const SORT_STORAGE_KEY = 'crm.notifications.sort';
const EMPTY_COUNTS: NotificationCounts = { all: 0, unread: 0, read: 0 };

export function NotificationSidebar({ open, onOpenChange }: NotificationSidebarProps) {
    const page = usePage();
    const isMobile = useIsMobile();
    const { t } = useTranslations();

    const tr = useCallback(
        (key: string, repl?: Record<string, string | number>): string => {
            let s = t(key);
            if (repl) {
                for (const [k, v] of Object.entries(repl)) {
                    s = s.replace(`:${k}`, String(v));
                }
            }
            return s;
        },
        [t],
    );

    const [mounted, setMounted] = useState(false);
    const [activeTab, setActiveTab] = useState<NotificationFilter>('unread');
    const [grouping, setGrouping] = useState<NotificationGrouping>('module');
    const [sort, setSort] = useState<NotificationSort>('newest');
    const [selectedModules, setSelectedModules] = useState<string[]>([]);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [facets, setFacets] = useState<NotificationModuleFacet[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [counts, setCounts] = useState<NotificationCounts>(EMPTY_COUNTS);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 20, total: 0, has_more: false });

    const activeToastIds = useRef<Set<string | number>>(new Set());

    useEffect(() => {
        setMounted(true);
        const stored = window.localStorage.getItem(GROUPING_STORAGE_KEY);
        if (stored === 'module' || stored === 'none') {
            setGrouping(stored);
        }

        const storedSort = window.localStorage.getItem(SORT_STORAGE_KEY);
        if (storedSort === 'newest' || storedSort === 'oldest') {
            setSort(storedSort);
        }
    }, []);

    const toggleSort = () => {
        const next: NotificationSort = sort === 'newest' ? 'oldest' : 'newest';
        setSort(next);
        window.localStorage.setItem(SORT_STORAGE_KEY, next);
    };

    const setGroupingPersisted = (next: NotificationGrouping) => {
        setGrouping(next);
        window.localStorage.setItem(GROUPING_STORAGE_KEY, next);
    };

    const fetchNotifications = useCallback(
        async (filter: NotificationFilter, modules: string[], order: NotificationSort, pageNumber = 1, append = false) => {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            try {
                const params = new URLSearchParams({ filter, sort: order, page: String(pageNumber), per_page: '20' });
                modules.forEach((module) => params.append('modules[]', module));

                const response = await fetch(`/notifications?${params.toString()}`, {
                    headers: { Accept: 'application/json' },
                });
                if (!response.ok) return;

                const data: NotificationResponse = await response.json();
                setNotifications((prev) => (append ? [...prev, ...data.data] : data.data));
                setPagination(data.pagination);
                setCounts(data.counts);
                setFacets(data.modules);
            } catch (error) {
                logger.log('Failed to load notifications', error);
            } finally {
                if (append) {
                    setLoadingMore(false);
                } else {
                    setLoading(false);
                }
            }
        },
        [],
    );

    const refresh = useCallback(() => {
        fetchNotifications(activeTab, selectedModules, sort);
        window.dispatchEvent(new CustomEvent('notifications-updated'));
    }, [activeTab, selectedModules, sort, fetchNotifications]);

    /**
     * Keep the chips and group headings honest after a row leaves the active
     * filter, without refetching the whole list under the user's cursor. A
     * module that empties out stops being offered, same as on a fresh load.
     */
    const adjustFacet = (moduleKey: string, delta: number) => {
        setFacets((prev) =>
            prev.map((facet) => (facet.key === moduleKey ? { ...facet, count: facet.count + delta } : facet)).filter((facet) => facet.count > 0),
        );
    };

    const markAsRead = async (id: string) => {
        try {
            const response = await csrfFetch(`/notifications/${id}/read`, { method: 'POST' });
            if (!response.ok) return;

            const leaving = activeTab === 'unread';
            if (leaving) adjustFacet(notifications.find((n) => n.id === id)?.module ?? '', -1);

            setNotifications((prev) =>
                leaving ? prev.filter((n) => n.id !== id) : prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)),
            );
            setCounts((await response.json()).counts ?? counts);
            window.dispatchEvent(new CustomEvent('notifications-updated'));
        } catch (error) {
            logger.log('Failed to mark notification as read', error);
        }
    };

    const markAsUnread = async (id: string) => {
        try {
            const response = await csrfFetch(`/notifications/${id}/unread`, { method: 'POST' });
            if (!response.ok) return;

            const leaving = activeTab === 'read';
            if (leaving) adjustFacet(notifications.find((n) => n.id === id)?.module ?? '', -1);

            setNotifications((prev) => (leaving ? prev.filter((n) => n.id !== id) : prev.map((n) => (n.id === id ? { ...n, read_at: null } : n))));
            setCounts((await response.json()).counts ?? counts);
            window.dispatchEvent(new CustomEvent('notifications-updated'));
        } catch (error) {
            logger.log('Failed to mark notification as unread', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            const response = await csrfFetch('/notifications/read-all', { method: 'POST' });
            if (response.ok) refresh();
        } catch (error) {
            logger.log('Failed to mark all notifications as read', error);
        }
    };

    /**
     * Removal is a soft delete, so the undo action simply restores whatever this
     * user cleared in the last minute — no id list has to survive in the toast.
     */
    const offerUndo = (message: string) => {
        toast.success(message, {
            position: 'top-center',
            duration: 8000,
            action: {
                label: t('notifications.remove.undo'),
                onClick: async () => {
                    try {
                        const response = await csrfFetch('/notifications/restore', { method: 'POST' });
                        if (!response.ok) return;
                        const { restored } = await response.json();
                        toast.success(tr('notifications.remove.restored', { count: restored }), { position: 'top-center' });
                        refresh();
                    } catch (error) {
                        logger.log('Failed to restore notifications', error);
                    }
                },
            },
        });
    };

    const removeOne = async (id: string) => {
        try {
            const response = await csrfFetch(`/notifications/${id}`, { method: 'DELETE' });
            if (!response.ok) {
                toast.error(t('notifications.errors.remove_failed'), { position: 'top-center' });
                return;
            }

            adjustFacet(notifications.find((n) => n.id === id)?.module ?? '', -1);
            setNotifications((prev) => prev.filter((n) => n.id !== id));
            setCounts((await response.json()).counts ?? counts);
            window.dispatchEvent(new CustomEvent('notifications-updated'));
            offerUndo(t('notifications.remove.removed_one'));
        } catch (error) {
            logger.log('Failed to remove notification', error);
        }
    };

    const clearRead = async () => {
        try {
            const response = await csrfFetch('/notifications/read', { method: 'DELETE' });
            if (!response.ok) return;

            const { removed } = await response.json();
            refresh();
            offerUndo(tr('notifications.remove.removed_many', { count: removed }));
        } catch (error) {
            logger.log('Failed to clear read notifications', error);
        }
    };

    const toggleModule = (key: string) => {
        setSelectedModules((prev) => (prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]));
    };

    const toggleCollapsed = (key: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const formatTimeAgo = (dateString: string) => {
        const diffInSeconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);

        if (diffInSeconds < 60) return t('notifications.time.just_now');
        if (diffInSeconds < 3600) return tr('notifications.time.minutes_ago', { n: Math.floor(diffInSeconds / 60) });
        if (diffInSeconds < 86400) return tr('notifications.time.hours_ago', { n: Math.floor(diffInSeconds / 3600) });
        if (diffInSeconds < 604800) return tr('notifications.time.days_ago', { n: Math.floor(diffInSeconds / 86400) });

        return formatDate(dateString);
    };

    const showNotificationToast = useCallback(
        ({ title, description, url }: { title: string; description?: string; url?: string | null }) => {
            const id = toast(title, {
                description,
                duration: 6000,
                position: 'top-right',
                action: url && url !== '#' ? { label: t('notifications.toast_view'), onClick: () => router.visit(url) } : undefined,
                onDismiss: () => activeToastIds.current.delete(id),
                onAutoClose: () => activeToastIds.current.delete(id),
            });
            activeToastIds.current.add(id);
        },
        [t],
    );

    useEffect(() => {
        const stop = router.on('start', () => {
            activeToastIds.current.forEach((id) => toast.dismiss(id));
            activeToastIds.current.clear();
        });

        return () => stop();
    }, []);

    // On the first homepage visit of a session, surface unread notifications as toasts.
    useEffect(() => {
        if ((page as { component?: string })?.component !== 'Homepage') return;

        const SESSION_KEY = 'crm.notifications.session_toasts_shown';
        if (typeof window === 'undefined' || window.sessionStorage.getItem(SESSION_KEY)) return;
        window.sessionStorage.setItem(SESSION_KEY, '1');

        (async () => {
            try {
                const response = await fetch('/notifications?filter=unread&page=1&per_page=5', { headers: { Accept: 'application/json' } });
                if (!response.ok) return;

                const data: NotificationResponse = await response.json();
                data.data.slice(0, 5).forEach((notification, index) => {
                    window.setTimeout(() => {
                        showNotificationToast({
                            title: `${notification.module_label} · ${notification.title}`,
                            description: formatTimeAgo(notification.created_at),
                            url: notification.url,
                        });
                    }, index * 400);
                });
            } catch (error) {
                logger.log('Failed to load session notification toasts', error);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, showNotificationToast]);

    // Real-time arrivals. The broadcast carries only a preview, so the list is
    // refetched rather than synthesised — a half-built row would have no module,
    // no title and no group to sit in.
    useEffect(() => {
        const userId = (page as { props?: { auth?: { user?: { id?: string | number } } } })?.props?.auth?.user?.id;
        if (!userId || !window.Echo) return;

        const channel = window.Echo.private(`private-user.${userId}`);

        channel.listen('NotificationCreated', (data: { preview?: string; url?: string; id: string; type: string }) => {
            showNotificationToast({
                title: data.preview || t('notifications.toast_fallback'),
                description: t('notifications.toast_new'),
                url: data.url,
            });

            if (open) {
                fetchNotifications(activeTab, selectedModules, sort);
            } else {
                setCounts((prev) => ({ ...prev, unread: prev.unread + 1, all: prev.all + 1 }));
            }

            window.dispatchEvent(new CustomEvent('notification-created', { detail: { id: data.id, type: data.type, preview: data.preview } }));
        });

        return () => {
            channel.stopListening('NotificationCreated');
        };
    }, [page, activeTab, selectedModules, sort, open, t, showNotificationToast, fetchNotifications]);

    useEffect(() => {
        if (open) fetchNotifications(activeTab, selectedModules, sort);
    }, [open, activeTab, selectedModules, sort, fetchNotifications]);

    useEffect(() => {
        if (!open) window.dispatchEvent(new CustomEvent('notifications-updated'));
    }, [open]);

    // Bucket the loaded page by module, in facet order (busiest first) so the
    // headings match the order of the filter chips above them.
    const groups = useMemo(() => {
        if (grouping === 'none') return null;

        const order = facets.map((f) => f.key);
        const buckets = new Map<string, Notification[]>();

        notifications.forEach((notification) => {
            const key = notification.module || 'general';
            buckets.set(key, [...(buckets.get(key) ?? []), notification]);
        });

        return [...buckets.entries()]
            .sort(([a], [b]) => (order.indexOf(a) === -1 ? 999 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 999 : order.indexOf(b)))
            .map(([key, items]) => ({
                key,
                label: facets.find((f) => f.key === key)?.label ?? items[0]?.module_label ?? key,
                total: facets.find((f) => f.key === key)?.count ?? items.length,
                items,
            }));
    }, [grouping, notifications, facets]);

    const handleTabChange = (value: string) => {
        setActiveTab(value as NotificationFilter);
        setNotifications([]);
        setSelectedModules([]);
        setPagination((prev) => ({ ...prev, current_page: 1 }));
    };

    const NotificationRow = ({ notification, showModule }: { notification: Notification; showModule: boolean }) => {
        const Icon = MODULE_ICONS[notification.module] ?? Bell;
        const isUnread = !notification.read_at;

        return (
            <div
                className={cn(
                    'hover:bg-muted/50 group relative cursor-pointer rounded-lg border p-3 transition-colors',
                    isUnread && 'border-info bg-info-subtle',
                )}
                onClick={() => {
                    if (isUnread) markAsRead(notification.id);
                    if (notification.url) window.location.href = notification.url;
                }}
            >
                <div className="flex items-start gap-3">
                    <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', isUnread ? 'text-info' : 'text-muted-foreground')} />
                    <div className="min-w-0 flex-1">
                        <p className={cn('text-sm break-words', isUnread && 'font-medium')}>
                            {showModule && <span className="text-muted-foreground">{notification.module_label} · </span>}
                            {notification.title}
                        </p>
                        <p className="text-muted-foreground mt-0.5 text-xs">{formatTimeAgo(notification.created_at)}</p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            title={isUnread ? t('notifications.mark_read') : t('notifications.mark_unread')}
                            onClick={(event) => {
                                event.stopPropagation();
                                void (isUnread ? markAsRead(notification.id) : markAsUnread(notification.id));
                            }}
                        >
                            {isUnread ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </Button>

                        {/* Only a notification already seen can be removed. */}
                        {!isUnread && (
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                title={t('notifications.remove.one')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    removeOne(notification.id);
                                }}
                            >
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const NotificationContent = () => (
        <div className="flex flex-1 flex-col gap-3">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="grid w-full grid-cols-3">
                    {(['unread', 'read', 'all'] as NotificationFilter[]).map((tab) => (
                        <TabsTrigger key={tab} value={tab} className="flex min-w-0 items-center justify-center gap-1.5 px-2">
                            <span className="truncate">{t(`notifications.tabs.${tab}`)}</span>
                            {counts[tab] > 0 && (
                                <Badge variant={tab === 'unread' ? 'destructive' : 'secondary'} className="h-5 min-w-5 shrink-0 px-1 text-xs">
                                    {counts[tab]}
                                </Badge>
                            )}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            {/* Chips are built from what the active filter actually holds, so a
                module with nothing in it is never offered. */}
            {facets.length > 1 && (
                <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5">
                    <Button
                        variant={selectedModules.length === 0 ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-7 shrink-0 rounded-full px-3 text-xs"
                        onClick={() => setSelectedModules([])}
                    >
                        {t('notifications.filter.all')}
                        <Badge variant="outline" className="ml-1.5 h-4 px-1 text-[10px]">
                            {counts[activeTab]}
                        </Badge>
                    </Button>
                    {facets.map((facet) => (
                        <Button
                            key={facet.key}
                            variant={selectedModules.includes(facet.key) ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-7 shrink-0 rounded-full px-3 text-xs"
                            onClick={() => toggleModule(facet.key)}
                        >
                            {facet.label}
                            <Badge variant="outline" className="ml-1.5 h-4 px-1 text-[10px]">
                                {facet.count}
                            </Badge>
                        </Button>
                    ))}
                </div>
            )}

            <ScrollArea className="min-h-0 flex-1">
                {loading ? (
                    <div className="space-y-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="rounded-lg border p-3">
                                <div className="flex items-start gap-3">
                                    <Skeleton className="h-8 w-8 rounded-full" />
                                    <div className="flex-1 space-y-2">
                                        <Skeleton className="h-4 w-3/4" />
                                        <Skeleton className="h-3 w-1/2" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <Bell className="text-muted-foreground mb-4 h-12 w-12" />
                        <h3 className="mb-2 text-lg font-medium">{t('notifications.empty_title')}</h3>
                        <p className="text-muted-foreground text-sm">
                            {selectedModules.length > 0
                                ? t('notifications.empty_filtered')
                                : activeTab === 'unread'
                                  ? t('notifications.empty_caught_up')
                                  : t('notifications.empty_none')}
                        </p>
                    </div>
                ) : groups ? (
                    <div className="space-y-4 pr-1">
                        {groups.map((group) => (
                            <div key={group.key}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground h-7 w-full justify-start px-1 text-[11px] font-semibold tracking-wider uppercase"
                                    onClick={() => toggleCollapsed(group.key)}
                                    aria-expanded={!collapsed.has(group.key)}
                                >
                                    {collapsed.has(group.key) ? <ChevronRight className="mr-1 h-3 w-3" /> : <ChevronDown className="mr-1 h-3 w-3" />}
                                    {group.label}
                                    <span className="text-muted-foreground/70 ml-1.5 normal-case">({group.total})</span>
                                </Button>
                                {!collapsed.has(group.key) && (
                                    <div className="mt-1 space-y-2">
                                        {group.items.map((notification) => (
                                            <NotificationRow key={notification.id} notification={notification} showModule={false} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-2 pr-1">
                        {notifications.map((notification) => (
                            <NotificationRow key={notification.id} notification={notification} showModule />
                        ))}
                    </div>
                )}

                {pagination.has_more && !loading && (
                    <div className="pt-3 pb-1">
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            disabled={loadingMore}
                            onClick={() => fetchNotifications(activeTab, selectedModules, sort, pagination.current_page + 1, true)}
                        >
                            {t('notifications.load_more')}
                        </Button>
                    </div>
                )}
            </ScrollArea>
        </div>
    );

    const NotificationFooter = () => (
        <div className="bg-background flex shrink-0 items-center justify-between gap-2 border-t px-4 py-3">
            <Button variant="ghost" size="sm" className="text-xs" onClick={toggleSort}>
                <ArrowDownUp className="mr-1.5 h-3.5 w-3.5" />
                {t(`notifications.sort.${sort}`)}
            </Button>
            <Button size="sm" className="text-xs" disabled={counts.unread === 0} onClick={markAllAsRead}>
                <CheckCheck className="mr-1.5 h-3.5 w-3.5" />
                {t('notifications.mark_all_read')}
            </Button>
        </div>
    );

    const HeaderActions = () => (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={t('notifications.title')}>
                    <MoreVertical className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setGroupingPersisted(grouping === 'module' ? 'none' : 'module')}>
                    <LayoutList className="mr-2 h-4 w-4" />
                    {grouping === 'module' ? t('notifications.group.flat') : t('notifications.group.grouped')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled={counts.read === 0} onClick={clearRead} variant="destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('notifications.remove.clear_read')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );

    if (!mounted) return null;

    if (isMobile) {
        return (
            <Drawer open={open} onOpenChange={onOpenChange}>
                <DrawerContent className="flex h-[90vh] flex-col">
                    <DrawerHeader className="flex flex-row items-center justify-between">
                        <DrawerTitle>{t('notifications.title')}</DrawerTitle>
                        <HeaderActions />
                    </DrawerHeader>
                    <div className="flex min-h-0 flex-1 flex-col px-4">
                        <NotificationContent />
                    </div>
                    <NotificationFooter />
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="flex w-full flex-col sm:max-w-md">
                {/* SheetContent renders its own close button at absolute top-4
                    right-4, so the actions menu is inset to clear it. */}
                <SheetHeader className="flex flex-row items-center justify-between space-y-0 pr-12">
                    <SheetTitle>{t('notifications.title')}</SheetTitle>
                    <HeaderActions />
                </SheetHeader>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4">
                    <NotificationContent />
                </div>
                <NotificationFooter />
            </SheetContent>
        </Sheet>
    );
}
